import { Link } from "react-router-dom";
import { Link as LinkIcon } from "lucide-react";
import { memo } from "react";
import { cn } from "@/lib/utils";
import { NO_DATA } from "@/constants";
import { EntityListLink } from "@/components/EntityListLink";

const linkBase =
  "inline-flex shrink-0 items-center text-blue-700 hover:underline underline-offset-4";
const containerBase =
  "flex h-full w-full min-w-0 items-center justify-start gap-1 px-2 pt-1";
const contentTruncate = "min-w-0 flex-1 truncate";

export interface EntityLinkProps {
  internalTo?: string;
  externalTo?: string;
  children?: React.ReactNode;
  className?: string;
}

export const EntityLink = memo(function EntityLink({
  internalTo,
  externalTo,
  children,
  className,
}: EntityLinkProps) {
  const content = children ?? NO_DATA;
  const hasInternal = !!internalTo;
  const hasExternal = !!externalTo;

  if (hasInternal && !hasExternal) {
    return (
      <Link
        to={internalTo}
        className={cn(
          "inline-flex min-w-0 w-full items-center gap-1.5 overflow-hidden",
          linkBase,
          className,
        )}
      >
        <LinkIcon className="h-4 w-4 shrink-0" aria-hidden />
        {content}
      </Link>
    );
  }

  if (hasExternal && !hasInternal) {
    return (
      <div className={cn(containerBase, className)}>
        <span className={contentTruncate}>{content}</span>
        <EntityListLink path={externalTo} className="h-6 w-6 shrink-0" />
      </div>
    );
  }

  if (hasInternal && hasExternal) {
    return (
      <div className={cn(containerBase, className)}>
        <Link
          to={internalTo}
          className={cn(
            linkBase,
            "min-w-0 flex-1 overflow-hidden flex items-center gap-1.5",
          )}
          aria-label="Open row"
        >
          <LinkIcon className="h-4 w-4 shrink-0" aria-hidden />
          <span className={contentTruncate}>{content}</span>
        </Link>
        <EntityListLink
          path={externalTo}
          className="h-6 w-6 shrink-0 text-black no-underline hover:no-underline"
        />
      </div>
    );
  }

  return <span className={className}>{content}</span>;
});
